import { useTranslation } from "react-i18next";
import { ReserveBadge } from "@/components/atoms/reserve-badge";
import { Checkbox } from "@/components/ui/checkbox";
import { type AvailabilitySlot } from "@/lib/backend/api";

interface AvailabilitySlotCardProps {
  slot: AvailabilitySlot;
  onSelect?: (slotId: string) => void;
  isSelected?: boolean;
}

export function AvailabilitySlotCard({ slot, onSelect, isSelected }: AvailabilitySlotCardProps) {
  const { t } = useTranslation();
  const startDate = new Date(slot.startAt);
  const endDate = new Date(slot.endAt);

  const formattedDate = startDate.toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("nl-NL", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const isAtCapacity = (slot.stats?.acceptedApplications ?? 0) >= slot.capacity;
  const isOnReserveList = slot.allowReserves && isAtCapacity;

  return (
    <div
      className="relative flex cursor-pointer items-center justify-between rounded-xl bg-surface-primary p-4 transition-colors hover:bg-surface-secondary"
      onClick={() => onSelect?.(slot.id)}
    >
      <div className="flex flex-col gap-2">
        <h3 className="font-bold capitalize text-txt-brand">{formattedDate}</h3>
        <div className="flex items-center gap-2 text-txt-primary">
          <span className="font-medium">
            {slot.userFunction?.name ?? t("common:all-functions")}
          </span>
          <span className="text-txt-secondary">•</span>
          <span>
            {formatTime(startDate)} - {formatTime(endDate)}
          </span>
        </div>
        {isOnReserveList && <ReserveBadge />}
      </div>

      <div className="bg-bg-brand flex items-center">
        <Checkbox
          className="h-6 w-6 bg-white text-txt-brand"
          checked={isSelected}
          onCheckedChange={() => onSelect?.(slot.id)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}
